import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { EM_DASH } from '~/common/constants/unicode';
import { trackSegment } from '~/components/SegmentAnalytics';
import { DescriptionListAccordion } from '~/eds/src/components/Accordion/subcomponents/DescriptionListAccordion';
import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { Toggle } from '~/eds/src/components/Toggle/Toggle';
import { INPUT_DEBOUNCE_MS } from '~/eds/src/constants/duration';
import { FileExtensionType } from '~/eds/src/enums/FileExtensionType';
import { useThrottledState } from '~/eds/src/hooks/useThrottledState';
import { PilotId as EdsPilotId } from '~/eds/src/types/PilotId';
import type {
  Field,
  Fields as FieldsType,
} from '~/features/document-handler/types/fields';
import { isWssField } from '~/features/document-handler/utils';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { getStorageItem, setStorageItem } from '~/utils/browser';

import { Field as FieldComponent } from './Field';
import { filterSections, testUsesHtmlToken } from './Fields.utils';

const FIELDS_EXPANDED_SECTIONS_KEY =
  'documentDetailsPanel.fields.expandedSections';

type Props = {
  activeFieldId?: EdsPilotId;
  enableFieldEditing?: boolean;
  docHandlerId?: PilotId;
  fileType?: FileExtensionType;
  onFieldHighlight?: (field: Field) => void;
  search?: string;
};

export const Fields = ({
  activeFieldId,
  enableFieldEditing = true,
  docHandlerId,
  fileType,
  onFieldHighlight,
  search: externalSearch,
}: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();
  const isWssFieldsEnabled = useFlag(FlagType.WSSDesignatedWorkflow);
  const isWSSIntegrationPhase2M1M3Enabled = useFlag(
    FlagType.WSSIntegrationPhase2M1M3,
  );
  const showWssFieldIndicator =
    isWssFieldsEnabled && !isWSSIntegrationPhase2M1M3Enabled;
  const hasDocumentDetailsPanelUpdate = useFlag(
    FlagType.DocumentDetailsPanelUpdate,
  );
  const { data, isFetching, isError } =
    api.endpoints.getDocumentInformation.useQueryState(
      { documentId: docHandlerId!, formatDate, formatNumber },
      {
        skip: !docHandlerId,
      },
    );
  const [throttledSearchInternal, searchInternal, setSearch] =
    useThrottledState('', INPUT_DEBOUNCE_MS);
  const throttledSearch = externalSearch ?? throttledSearchInternal;
  const search = externalSearch ?? searchInternal;
  const documentId = data?.documentId;

  const [showEmptyFields, setShowEmptyFields] = useState(false);
  const [editingSections, setEditingSections] = useState<
    Record<string, boolean>
  >({});
  const [sectionPendingValues, setSectionPendingValues] = useState<
    Record<string, Record<number, { label: string; value: any }>>
  >({});
  const [isSavingSection, setIsSavingSection] = useState<
    Record<string, boolean>
  >({});
  const { toast } = useToast();
  const [updateDocument] = api.endpoints.updateDocument.useMutation();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => getStorageItem(FIELDS_EXPANDED_SECTIONS_KEY) ?? {});
  const handleSectionToggle = (
    isExpanded: boolean,
    item: { title: string },
  ) => {
    trackSegment('selectAccordion', {
      name: documentId,
      value: item.title,
      isExpanded,
    });

    setExpandedSections((prev) => {
      const updated = { ...prev, [item.title]: isExpanded };
      setStorageItem(FIELDS_EXPANDED_SECTIONS_KEY, updated);
      return updated;
    });
  };

  const isSectionExpanded = (title: string) => {
    return expandedSections[title] ?? !hasDocumentDetailsPanelUpdate;
  };

  const toggleSectionEditing = (sectionTitle: string) => {
    setEditingSections((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }));
  };

  const cancelSectionEditing = (sectionTitle: string) => {
    setEditingSections((prev) => ({ ...prev, [sectionTitle]: false }));
    setSectionPendingValues((prev) => {
      const updated = { ...prev };
      delete updated[sectionTitle];
      return updated;
    });
  };

  const handleSectionSave = async (sectionTitle: string) => {
    if (!docHandlerId) return;
    const pendingValues = sectionPendingValues[sectionTitle] || {};
    const fieldsToSave = Object.entries(pendingValues).map(
      ([id, { label, value }]) => ({
        id: Number(id),
        label,
        value,
      }),
    );
    if (fieldsToSave.length === 0) {
      setEditingSections((prev) => ({ ...prev, [sectionTitle]: false }));
      return;
    }
    setIsSavingSection((prev) => ({ ...prev, [sectionTitle]: true }));
    try {
      await updateDocument({
        documentId: docHandlerId,
        fields: fieldsToSave,
      }).unwrap();
      toast({
        message: intl.formatMessage({
          id: 'Fields.sectionSavedSuccessfully',
          defaultMessage: 'Section saved successfully',
          description: 'Success message when a section is saved',
        }),
        status: 'success',
      });
      setEditingSections((prev) => ({ ...prev, [sectionTitle]: false }));
      setSectionPendingValues((prev) => {
        const updated = { ...prev };
        delete updated[sectionTitle];
        return updated;
      });
    } catch {
      toast({
        message: intl.formatMessage({
          id: 'Fields.sectionSaveError',
          defaultMessage: 'An error occurred while saving the section',
          description: 'Error message when section save fails',
        }),
        status: 'danger',
      });
    } finally {
      setIsSavingSection((prev) => ({ ...prev, [sectionTitle]: false }));
    }
  };

  useEffect(() => {
    if (throttledSearch) {
      trackSegment('searchFields', {
        name: documentId,
        value: throttledSearch,
      });
    }
  }, [throttledSearch, documentId]);

  const handleSearch = (text: Nullable<string>) => {
    setSearch(text ?? '');
  };

  const hasHighlight = (field: Field) => {
    if (!onFieldHighlight || !fileType) return false;
    if (testUsesHtmlToken(fileType)) {
      return field.htmlTokens.length > 0;
    } else {
      return field.coordinates.length > 0;
    }
  };

  const toAccordionTerm = (
    field: Field,
    sectionTitle: string,
    isEditingSection: boolean,
  ) => {
    return {
      title: field.name,
      description: (
        <FieldComponent
          field={field}
          enableFieldEditing={enableFieldEditing}
          documentId={docHandlerId!}
          isActive={
            field.id ===
            (typeof activeFieldId === 'string'
              ? parseInt(activeFieldId)
              : activeFieldId)
          }
          sectionEditMode={
            hasDocumentDetailsPanelUpdate &&
            isEditingSection &&
            field.isEditable &&
            enableFieldEditing
          }
          onValueChange={(id, label, value) => {
            setSectionPendingValues((prev) => ({
              ...prev,
              [sectionTitle]: {
                ...(prev[sectionTitle] || {}),
                [id]: { label, value },
              },
            }));
          }}
        />
      ),
      actions: [
        ...(showWssFieldIndicator && isWssField(field)
          ? [
              {
                icon: 'database',
                tooltip: intl.formatMessage({
                  id: 'Fields.sourceSS',
                  defaultMessage: 'Source: Strategic Sourcing',
                  description:
                    'Tooltip for fields sourced from Strategic Sourcing',
                }),
                mode: 'icon',
              },
            ]
          : []),
        ...(hasHighlight(field)
          ? [
              {
                text: intl.formatMessage({
                  id: 'Fields.highlightField',
                  defaultMessage: 'Highlight field',
                  description: 'Button text to highlight field',
                }),
                icon: 'search-gradient',
                tooltip: intl.formatMessage({
                  id: 'Fields.highlightFieldTooltip',
                  defaultMessage: 'Highlight field',
                  description: 'Tooltip for highlight field button',
                }),
                mode: 'icon',
                onClick: () => onFieldHighlight?.(field),
              },
            ]
          : []),
      ],
      tooltip: field.helpText,
    };
  };

  const toAccordionSection = (section: FieldsType['sections'][number]) => {
    const fields =
      !hasDocumentDetailsPanelUpdate || showEmptyFields
        ? section.fields
        : section.fields.filter((field) => field.displayValue !== EM_DASH);
    const isEditingSection = !!editingSections[section.title];
    const isSaving = !!isSavingSection[section.title];
    return {
      title: section.title,
      terms: fields.map((field) =>
        toAccordionTerm(field, section.title, isEditingSection),
      ),
      isExpanded: isSectionExpanded(section.title),
      ...(hasDocumentDetailsPanelUpdate && {
        edit: {
          isEditing: isEditingSection,
          isSaving,
          onClick: () => toggleSectionEditing(section.title),
          onCancel: () => cancelSectionEditing(section.title),
          onSave: () => handleSectionSave(section.title),
        },
      }),
    };
  };
  const sections = useMemo(() => {
    if (isError || !data) return [];

    return data.sections.map(toAccordionSection);
  }, [
    isError,
    data,
    activeFieldId,
    expandedSections,
    showEmptyFields,
    enableFieldEditing,
    editingSections,
    isSavingSection,
    sectionPendingValues,
  ]);

  const filteredSections = useMemo(() => {
    return filterSections(sections, search);
  }, [sections, search]);

  const loadingContent = {
    isLoading: isFetching,
    message: intl.formatMessage({
      id: 'Fields.loadingMessage',
      defaultMessage: 'Loading fields',
      description: 'Loading message for fields',
    }),
  };
  const placeholderContent = isError
    ? {
        icon: 'field-clause' as const,
        message: intl.formatMessage({
          id: 'Fields.errorMessage',
          defaultMessage:
            'Something went wrong when retrieving the fields. Please refresh and load again or contact your admin.',
          description: 'Error message when fields fail to load',
        }),
      }
    : filteredSections.length === 0 && search
      ? {
          title: intl.formatMessage({
            id: 'Fields.noMatchingFields',
            defaultMessage: 'No matching fields',
            description: 'Title when search yields no results',
          }),
          image: 'empty-no-search',
        }
      : undefined;

  return (
    <Layout h="100%" preset="sections" pt={4}>
      {' '}
      {!hasDocumentDetailsPanelUpdate && (
        <SearchInput
          disabled={isError || isFetching}
          placeholder={intl.formatMessage({
            id: 'Fields.searchPlaceholder',
            defaultMessage: 'Search fields',
            description: 'Placeholder text for field search input',
          })}
          name="search"
          onChange={(text) => {
            handleSearch(text);
          }}
          value={search}
        />
      )}
      {hasDocumentDetailsPanelUpdate && (
        <Layout align="center" justify="flex-end" spacing={2}>
          <Text variant="tiny" color="text.secondary">
            {intl.formatMessage({
              id: 'Fields.showEmptyFields',
              defaultMessage: 'Show Empty Fields',
              description: 'Label for the show empty fields toggle',
            })}
          </Text>
          <Toggle
            name="showEmptyFields"
            enabled={showEmptyFields}
            onToggle={setShowEmptyFields}
          />
        </Layout>
      )}
      <Box overflowY="auto" styles={{ '& dd': { minWidth: 0, flex: 1 } }}>
        <ContentContainer
          loadingContent={loadingContent}
          placeholderContent={placeholderContent}
        >
          <DescriptionListAccordion
            isEmbedded={hasDocumentDetailsPanelUpdate ? true : undefined}
            isIndented={!hasDocumentDetailsPanelUpdate}
            columns={hasDocumentDetailsPanelUpdate ? undefined : 'auto-fit'}
            columnsMinWidth={hasDocumentDetailsPanelUpdate ? undefined : 200}
            mode={hasDocumentDetailsPanelUpdate ? 'row' : undefined}
            items={filteredSections}
            search={search}
            onToggle={handleSectionToggle}
          />
        </ContentContainer>
      </Box>
    </Layout>
  );
};
